// cmp_app_shell — the SINGLE shell (ADR-014 §2, SEM-5131 owner decision
// 2026-07-30: the dual-role hat is gone). Three-region layout: left rail
// (one grouped menu, filtered by the caller's real authority inside
// LeftRail) + top bar + content slot + the AI assistant panel. There is NO
// AdminAppShell and no role parameter — every route mounts the same shell and
// the same <Outlet/>.
//
// The assistant mounted here is the CAPABILITY-DISPATCHED one (2026-08-04):
// the Faz-0 AiDrawer that used to sit in this slot posted to /v1/ai/stream
// without a capability_id — the unmanaged raw-prompt path, flag-off
// everywhere, so it drew an error state on every screen. The managed panel
// was previously reachable only from /help; the shell is where it belongs.

import { useRef } from "react";
import { Outlet } from "react-router";

import { AdminSetupLaunch } from "@/features/admin-setup/setup-launch";
import { EnvironmentBanner } from "@/features/admin-environments/environment-banner";
import { SupportAssistantRail } from "@/features/support-assistant";
import { useScrollRestoration } from "@/lib/use-scroll-restoration";
import { PageCallout, ScreenTour, ShellTour, TourProvider } from "@/features/tour";

import { LeftRail } from "./LeftRail";
import { TabStrip } from "./TabStrip";
import { TopBar } from "./TopBar";

export function AppShell(): React.ReactElement {
  // <main> is the page scroller, not the window (SEM-6350). A screen with its
  // OWN inner scroll region (a windowed table) scrolls that region instead, so
  // it is not covered here.
  const mainRef = useRef<HTMLElement>(null);
  useScrollRestoration(mainRef);

  return (
    // The tour wraps the whole shell because every one of its surfaces lives
    // here: the shell tour's five anchors are the rail and the top bar, the
    // top bar's "?" reopens it, and the per-page callout is rendered ONCE
    // above <Outlet/> rather than opted into by 40 routes.
    <TourProvider>
      <AdminSetupLaunch />
      <div className="flex h-full">
        <LeftRail />
        {/* `min-w-0`: bir flex çocuğunun varsayılan `min-width:auto` değeri onu
            içeriğinin en dar hâlinden daha küçük olmaya bırakmaz. Bu sütun
            tabloları taşıdığı için o taban 900px'e çıkabiliyordu ve sayfa
            yanlamasına kayıyordu (SEM-4928). Küçülme izni burada veriliyor,
            kaydırma tablonun kendi kapsayıcısında yapılıyor. */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Above TopBar and outside <main>: the sandbox label must survive
              every route change and every inner scroll region. */}
          <EnvironmentBanner />
          <TopBar />
          <TabStrip />
          <div className="flex flex-1 overflow-hidden">
            {/* novem canvas — warm oat app background (mockup --bg-app);
                raised surfaces (cards) sit on it in white. */}
            {/* Telefonda 2×28px kenar boşluğu ekranın %15'ini yerdi. */}
            <main
              className="min-w-0 flex-1 overflow-auto bg-[var(--bg-surface)] px-4 py-5 md:px-7 md:py-[30px]"
              id="main-content"
              ref={mainRef}
            >
              <PageCallout />
              <Outlet />
            </main>
            {/* Fixed-position overlay panel — it takes no layout space here. */}
            <SupportAssistantRail />
          </div>
        </div>
      </div>
      <ShellTour />
      <ScreenTour />
    </TourProvider>
  );
}
